import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion } from "motion/react"
import FavoriteItem from "../../../../features/profile/FavoriteItem"
import { useRequest } from "../../../../hooks/useRequest"
import { openAuth } from "../../../../lib/openAuth"
import { appStore } from "../../../../store/appStore"
import { userStore } from "../../../../store/userStore"
import { FavoriteMovie } from "../../../../types/entities/favorite"
import { useCinemaHref } from "../../../../hooks/useCinemaHref"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

export default function Favourites() {
    const { request } = useRequest()
    const { user } = userStore()
    const { cinema } = appStore()
    const navigate = useNavigate()
    const href = useCinemaHref()
    const [items, setItems] = useState<FavoriteMovie[]>([])
    const [loading, setLoading] = useState(false)
    const [removingId, setRemovingId] = useState<number | null>(null)

    const loadFavorites = async () => {
        const data = await request<FavoriteMovie[]>("GET", `user/favorite?cinema_id=${cinema.id}`)
        setItems(data)
    }

    const removeFavorite = async (filmId: number) => {
        setRemovingId(filmId)
        try {
            await request("DELETE", "user/favorite", { film_id: filmId })
            setItems(prev => prev.filter(item => item.id !== filmId))
        } finally {
            setRemovingId(null)
        }
    }

    useEffect(() => {
        if (!user) {
            openAuth(navigate, { pathname: "/profile" }, { replace: true })
            return
        }
        setLoading(true)
        loadFavorites().finally(() => setLoading(false))
    }, [user, cinema.id])

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <motion.div
                    className="size-10 rounded-full border-2 border-white/10 border-t-[#349be6]"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, ease: "linear", repeat: Infinity }}
                />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <h2
                className="text-white"
                style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 24, letterSpacing: "-0.01em" }}>
                ❤️ Избранное
            </h2>

            {items.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-16 text-center">
                    <span className="text-[56px]">💔</span>
                    <p className="text-white/40 text-[15px]" style={{ fontFamily: BODY }}>
                        Нет избранных фильмов
                    </p>
                    <Link
                        to={href("/afisha")}
                        className="text-[#349be6] text-[13px] underline"
                        style={{ fontFamily: MONO, fontWeight: 700 }}>
                        Перейти в афишу
                    </Link>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {items.map((item, i) => (
                        <FavoriteItem
                            key={item.id}
                            item={item}
                            onRemove={removeFavorite}
                            removing={removingId === item.id}
                            index={i}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
